import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingCart } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewOrderPage = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [quantity, setQuantity] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/services`);
      setServices(data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoadingServices(false);
    }
  };

  const calculatePrice = () => {
    if (!selectedService || !quantity) return 0;
    return (selectedService.price_per_unit * parseFloat(quantity)).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      toast.error('Selecciona un servicio');
      return;
    }

    const qty = parseInt(quantity);
    if (qty < selectedService.min_quantity || qty > selectedService.max_quantity) {
      toast.error(`La cantidad debe estar entre ${selectedService.min_quantity} y ${selectedService.max_quantity}`);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/orders`,
        {
          service_id: selectedService.id,
          target_url: targetUrl,
          quantity: qty,
          coupon_code: couponCode || undefined,
        },
        { withCredentials: true }
      );
      toast.success('¡Pedido creado exitosamente!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear el pedido';
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.platform]) {
      acc[service.platform] = [];
    }
    acc[service.platform].push(service);
    return acc;
  }, {});

  if (loadingServices) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="new-order-title">
            Crear Nuevo Pedido
          </h1>
          <p className="text-muted-foreground">
            Selecciona un servicio y completa los detalles
          </p>
        </div>

        <Card className="p-8 border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="platform">Plataforma</Label>
              <Select
                onValueChange={(value) => {
                  const service = services.find((s) => s.id === value);
                  setSelectedService(service);
                  setQuantity('');
                }}
                data-testid="service-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un servicio" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groupedServices).map(([platform, platformServices]) => (
                    <React.Fragment key={platform}>
                      <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                        {platform}
                      </div>
                      {platformServices.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} - ${service.price_per_unit}/u
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedService && (
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold mb-2">{selectedService.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{selectedService.description}</p>
                <p className="text-sm">
                  <span className="font-medium">Cantidad:</span> {selectedService.min_quantity} - {selectedService.max_quantity}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Precio:</span> ${selectedService.price_per_unit} por unidad
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="targetUrl">URL del perfil o publicación</Label>
              <Input
                id="targetUrl"
                type="url"
                placeholder="https://instagram.com/tu-perfil"
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                required
                data-testid="target-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Ingresa el enlace completo de tu perfil o publicación
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Cantidad</Label>
              <Input
                id="quantity"
                type="number"
                placeholder="Ej: 1000"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min={selectedService?.min_quantity}
                max={selectedService?.max_quantity}
                required
                disabled={!selectedService}
                data-testid="quantity-input"
              />
              {selectedService && (
                <p className="text-xs text-muted-foreground">
                  Mínimo: {selectedService.min_quantity}, Máximo: {selectedService.max_quantity}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="couponCode">Código de cupón (opcional)</Label>
              <Input
                id="couponCode"
                type="text"
                placeholder="DESCUENTO10"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                data-testid="coupon-input"
              />
            </div>

            {quantity && selectedService && (
              <div className="p-6 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Total a pagar:</span>
                  <span className="text-3xl font-bold text-primary">${calculatePrice()}</span>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loading || !selectedService}
              data-testid="submit-order-button"
            >
              {loading ? (
                'Procesando...'
              ) : (
                <>
                  <ShoppingCart size={20} className="mr-2" />
                  Crear Pedido
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};